import { getFieldConfig } from "./index";
import { isBlankValue, normalizeTrimmedValue } from "@validationUtility";
import { shouldRetainPackageRow } from "./packageRowUtils";
import {
  IPackagingFieldsConfig,
  PACKAGING_FIELDS,
  isPackageDetailsRowField,
} from "./types";

interface IPackageItem {
  hu?: string | number | null;
  packageCode?: string | null;
  loosePieces?: string | number | null;
}

interface IPackageError {
  hu: string | null;
  packageCode: string | null;
  loosePieces: string | null;
}

interface IWeightContext {
  validateWeightVsHazmat?: (value: string | number) => boolean;
}

interface IFieldContext {
  totalHUCount?: number;
  validateWeightVsHazmat?: (value: string | number) => boolean;
}

export const createPackagingValidator = (
  packagingConfig: IPackagingFieldsConfig
) => {
  const validateRequired = (
    configKey: string,
    value: any
  ): string | null => {
    const fieldCfg = getFieldConfig(packagingConfig, configKey);
    if (!fieldCfg.visible || !fieldCfg.required) return null;
    const isEmptyValue = isBlankValue(value);
    return isEmptyValue ? `* ${fieldCfg.label} is required` : null;
  };

  // Returns an error when a numeric value falls outside the field's configured minValue/maxValue.
  // Empty values are skipped here — the required check handles those.
  const validateNumericBounds = (
    configKey: string,
    value: any
  ): string | null => {
    const isEmptyValue = isBlankValue(value);
    if (isEmptyValue) return null;
    const numericValue = Number(value);
    if (isNaN(numericValue)) return null;
    const fieldCfg = getFieldConfig(packagingConfig, configKey);
    if (fieldCfg.minValue !== undefined && numericValue < fieldCfg.minValue) {
      return `* ${fieldCfg.label} cannot be less than ${fieldCfg.minValue}`;
    }
    if (fieldCfg.maxValue !== undefined && numericValue > fieldCfg.maxValue) {
      return `* ${fieldCfg.label} cannot be greater than ${fieldCfg.maxValue}`;
    }
    return null;
  };

  const validatePieces = (
    value: string | number | null | undefined,
    totalHUCount: number = 0
  ): string | null => {
    const cfg = getFieldConfig(packagingConfig, PACKAGING_FIELDS.PIECES);
    if (!cfg.visible) return null;
    const isEmptyValue = isBlankValue(value);
    if (!cfg.required && isEmptyValue) return null;
    if (isEmptyValue) return `* ${cfg.label} is required`;
    if (parseInt(String(value)) < totalHUCount) {
      const huLabel = getFieldConfig(packagingConfig, PACKAGING_FIELDS.HU).label;
      return `${cfg.label} should be equal or more than total ${huLabel.toLowerCase()} (${totalHUCount})`;
    }
    return validateNumericBounds(PACKAGING_FIELDS.PIECES, value);
  };

  const validateWeight = (
    value: string | number | null | undefined,
    { validateWeightVsHazmat }: IWeightContext = {}
  ): string | null => {
    const cfg = getFieldConfig(packagingConfig, PACKAGING_FIELDS.WEIGHT);
    if (!cfg.visible) return null;
    const isEmptyValue = isBlankValue(value);
    if (!cfg.required && isEmptyValue) return null;
    if (isEmptyValue) return `* ${cfg.label} is required`;
    if (validateWeightVsHazmat && !validateWeightVsHazmat(value)) {
      return `${cfg.label} of the bill cannot be lesser than total hazmat weight`;
    }
    return validateNumericBounds(PACKAGING_FIELDS.WEIGHT, value);
  };

  const getPackagingFieldError = (
    field: string,
    value: any,
    context: IFieldContext = {}
  ): string | null => {
    switch (field) {
      case PACKAGING_FIELDS.PIECES:
        return validatePieces(value, context.totalHUCount);
      case PACKAGING_FIELDS.WEIGHT:
        return validateWeight(value, {
          validateWeightVsHazmat: context.validateWeightVsHazmat,
        });
      case PACKAGING_FIELDS.LOOSE_PIECES:
        return validateRequired(PACKAGING_FIELDS.LOOSE_PIECES, value)
          || validateNumericBounds(PACKAGING_FIELDS.LOOSE_PIECES, value);
      case PACKAGING_FIELDS.HU:
        return validateRequired(PACKAGING_FIELDS.HU, value)
          || validateNumericBounds(PACKAGING_FIELDS.HU, value);
      case "packageCode":
        return validateRequired(
          PACKAGING_FIELDS.PACKAGE_TYPE,
          normalizeTrimmedValue(value)
        );
      default:
        return null;
    }
  };

  const validatePackages = (
    packages: IPackageItem[]
  ): IPackageError[] => {
    const huCfg = getFieldConfig(packagingConfig, PACKAGING_FIELDS.HU);
    const pkgCfg = getFieldConfig(packagingConfig, PACKAGING_FIELDS.PACKAGE_TYPE);
    const loosePiecesCfg = packagingConfig.fieldMap[PACKAGING_FIELDS.LOOSE_PIECES];
    const loosePiecesOnRow =
      loosePiecesCfg !== undefined &&
      isPackageDetailsRowField(loosePiecesCfg);

    if (!huCfg.visible && !pkgCfg.visible && !loosePiecesOnRow) return [];

    let hasError = false;
    const hasAtleastOneValue = Boolean(
      packages?.reduce(
        (str: string, item: IPackageItem) =>
          `${str}${item.hu?.toString() ?? ""}${item.packageCode ?? ""}`.trim(),
        ""
      )
    );

    const errors: IPackageError[] = packages?.map(
      (item: IPackageItem, index: number) => {
        let itemError: IPackageError = {
          hu: null,
          packageCode: null,
          loosePieces: null,
        };
        const shouldValidate =
          packages.length === 1 ||
          (!hasAtleastOneValue && index === 0) ||
          shouldRetainPackageRow(item, packagingConfig);

        if (shouldValidate) {
          itemError = {
            hu: huCfg.visible
              ? getPackagingFieldError(PACKAGING_FIELDS.HU, item.hu)
              : null,
            packageCode: pkgCfg.visible
              ? getPackagingFieldError("packageCode", item.packageCode)
              : null,
            loosePieces: loosePiecesOnRow
              ? getPackagingFieldError(
                  PACKAGING_FIELDS.LOOSE_PIECES,
                  item.loosePieces
                )
              : null,
          };
        }
        hasError =
          hasError ||
          Boolean(
            itemError.hu || itemError.packageCode || itemError.loosePieces
          );
        return itemError;
      }
    );

    return hasError ? errors : [];
  };

  return { getPackagingFieldError, validatePackages };
};
